import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const CATS = ["All", "Food", "Buffet", "Weddings", "Events", "Live Counters", "Service"];

export const Gallery = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const items = GALLERY.filter((g) => cat === "All" || g.cat === cat);

  const step = (dir) => setActive((a) => (a === null ? null : (a + dir + items.length) % items.length));

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-cream py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionHeading chapter="07" eyebrow="Gallery" title="Moments From" script="our celebrations." />

        <Reveal className="mt-12 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { setCat(c); setActive(null); }}
              className={`px-5 py-2 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
                cat === c ? "border-espresso bg-espresso text-cream" : "border-espresso/20 text-espresso/60 hover:border-gold hover:text-gold-dark"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="mt-10 columns-2 md:columns-3 gap-4 [column-fill:balance]">
          {items.map((g, i) => (
            <Reveal key={g.img + g.cat} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                className="group relative block w-full overflow-hidden"
              >
                <img
                  src={g.img}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1.2s] ease-out"
                />
                <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/35 transition-colors duration-500" />
                <span className="absolute bottom-3 left-3 text-cream text-[10px] tracking-[0.25em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {g.cat}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-espresso/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
            onClick={() => setActive(null)}
          >
            <motion.img
              key={items[active].img}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              src={items[active].img}
              alt={items[active].alt}
              className="max-w-full max-h-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button data-testid="lightbox-close" onClick={() => setActive(null)} aria-label="Close" className="absolute top-5 right-5 text-cream/70 hover:text-gold"><X size={30} /></button>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-cream/70 hover:text-gold"><ChevronLeft size={38} /></button>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-cream/70 hover:text-gold"><ChevronRight size={38} /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
